/* product-rating-link.js — <product-rating-link>

   Turns the PDP star row (snippets/product-rating.liquid) into a jump link to
   Judge.me's review list.

   The target cannot be hardcoded. For Online Store 2.0 themes Judge.me's own
   documented path is to add the Review Widget as an **app block** from the theme
   editor, so the surrounding DOM id belongs to Shopify's app-block wrapper rather
   than to us, and the widget itself is injected by Judge.me's script after first
   paint. The anchor therefore ships with a nominal href and this element resolves
   the real node at runtime.

   If no widget ever shows up — the app block was never added to the product
   template, or Judge.me changed their markup — the anchor is unwrapped back into
   the same static span the snippet emits when it is given no href, so the row
   still reads as a rating instead of being a link that goes nowhere.

   Selector order is priority order, and each one is tried separately: a plain
   `document.querySelectorAll` of the whole list returns document order, which
   would let an unrelated `.jdgm-widget` earlier in the page beat the actual
   review widget below it. */

const WIDGET_SELECTORS = ['#judgeme_product_reviews', '.jdgm-review-widget', '.jdgm-rev-widg', '.jdgm-widget'];

/* Judge.me's script injects the widget after first paint, so the node is
   routinely absent at connect time. Watch for it, then give up and unwrap. */
const RESOLVE_TIMEOUT = 8000;

/* Breathing room between the sticky header and the widget once scrolled. */
const SCROLL_GAP = 24;

class ProductRatingLink extends HTMLElement {
  connectedCallback() {
    this.link = this.querySelector('a');
    if (!this.link) return;

    this.link.addEventListener('click', this.handleClick);

    if (this.resolveWidget()) return;
    this.waitForWidget();
  }

  disconnectedCallback() {
    this.link?.removeEventListener('click', this.handleClick);
    this.stopWaiting();
  }

  resolveWidget() {
    for (const selector of WIDGET_SELECTORS) {
      // Skip our own subtree so the row can never resolve to itself.
      const found = Array.from(document.querySelectorAll(selector)).find((node) => !this.contains(node));
      if (found) {
        this.widget = found;
        return true;
      }
    }

    return false;
  }

  waitForWidget() {
    this.observer = new MutationObserver(() => {
      if (this.resolveWidget()) this.stopWaiting();
    });
    this.observer.observe(document.body, { childList: true, subtree: true });

    this.timer = setTimeout(() => {
      this.stopWaiting();
      if (!this.widget) this.unwrap();
    }, RESOLVE_TIMEOUT);
  }

  stopWaiting() {
    this.observer?.disconnect();
    this.observer = null;
    clearTimeout(this.timer);
  }

  handleClick = (event) => {
    // Resolve late as well — a click can land before Judge.me has injected.
    if (!this.widget && !this.resolveWidget()) {
      // Nothing to jump to; swallow the click rather than dirtying the URL with a
      // hash that matches no element.
      event.preventDefault();
      return;
    }

    event.preventDefault();

    const header = document.querySelector('sticky-header');
    const headerHeight = header?.getBoundingClientRect().height || 0;
    const target = this.widget.getBoundingClientRect().top + window.scrollY - headerHeight - SCROLL_GAP;

    window.scrollTo({
      top: Math.max(target, 0),
      behavior: window.Theme?.config?.motionReduced ? 'auto' : 'smooth',
    });

    /* Keyboard and screen-reader users need focus to follow the scroll, otherwise
       the next Tab continues from the rating row at the top of the page. The
       widget is not focusable on its own, so make it programmatically focusable
       without adding it to the tab order. */
    if (!this.widget.hasAttribute('tabindex')) this.widget.setAttribute('tabindex', '-1');
    this.widget.focus({ preventScroll: true });
  };

  /* Mirror the static branch of snippets/product-rating.liquid exactly: a span
     with role="img" carrying the same accessible name. */
  unwrap() {
    if (!this.link) return;

    const span = document.createElement('span');
    span.className = this.link.className;
    span.setAttribute('role', 'img');

    const label = this.link.getAttribute('aria-label');
    if (label) span.setAttribute('aria-label', label);

    span.replaceChildren(...this.link.childNodes);
    this.link.removeEventListener('click', this.handleClick);
    this.link.replaceWith(span);
    this.link = null;
  }
}

if (!customElements.get('product-rating-link')) {
  customElements.define('product-rating-link', ProductRatingLink);
}
